import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type RefObject } from 'react';
import { useAuth } from '../auth/AuthContext';
import { readyFileStore } from '../download';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import { Icon, type IconName } from './icons';
import { stationExistsOnDevice } from '../helperStations/localStation';
import { intakeExistsOnDevice } from '../intake/localIntake';
import { readDirectStart, writeDirectStart } from '../pos/directStart';
import { isNativeApp } from '../native';
import { useBackgroundSend } from '../pos/backgroundSend';
import { countUnsentSales, lastPosEvents } from '../pos/sessions';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { ConsentBanner } from '../analytics/ConsentBanner';
import { useAnalytics, useAnalyticsPageViews } from '../analytics/useAnalytics';
import { ConnectivityBar } from './ConnectivityBar';
import { ContextSwitcher } from './ContextSwitcher';
import { useNavContext, useNavContextOrLast } from './navContext';
import { CASH_PAGE, PAIR_PAGE, WORK_PAGE } from './workPages';
import { useNavRoles, type NavRoles } from '../hooks/useNavRoles';
import { MoreSheet } from './MoreSheet';
import { navModel, type NavModel } from './navModel';
import { THEME_CHOICES, THEME_ICONS, THEME_LABELS, setTheme, useThemeChoice } from '../lib/theme';

/**
 * Rollen im Browser: nicht abgefragt (kein Mehraufwand für jede Seite), also unbekannt. Die untere Leiste zeigt dann den
 * Normalfall (Start, Anmeldungen, Einsätze, Nachrichten, Mehr); „Kasse“ kommt nur über die gemerkte Kasse des Basars.
 */
const BROWSER_ROLES: NavRoles = { events: [], registrationCount: null, managedOrgCount: null };

/** Arbeitsseiten mit Scanfläche/Warenkorb: die mobile Tableiste würde dort nur Platz wegnehmen (Baustein 8). */
const HIDE_TABBAR = {
  test: (path: string): boolean => WORK_PAGE.test(path) || PAIR_PAGE.test(path),
};

/** Hauptnavigation für angemeldete Personen: oben (breit) und als Tableiste unten (Handy) dieselben Ziele. */
const MAIN_NAV: { to: string; label: string; short: string; icon: IconName }[] = [
  { to: '/', label: 'Start', short: 'Start', icon: 'home' },
  { to: '/meine-anmeldungen', label: 'Meine Anmeldungen', short: 'Anmeldungen', icon: 'tag' },
  { to: '/einsaetze', label: 'Einsätze', short: 'Einsätze', icon: 'calendar' },
  { to: '/profil', label: 'Profil', short: 'Profil', icon: 'user' },
];

/** Kopfzeile (eine Zeile) und Tableiste: die häufig genutzten Ziele; Profil und Neuigkeiten stehen im Konto-Menü. */
const HEADER_NAV = MAIN_NAV.filter((entry) => entry.to !== '/profil');

/**
 * Ziel des Reiters „Kasse“ am Handy: die Kasse des geöffneten Basars, wenn die Person dort kassieren darf, sonst
 * die zuletzt benutzte Kasse dieses Geräts (mit Schnellzugang führt sie auf die Geräte-Seite). Ohne beides kein Reiter.
 * Im geöffneten Basar entscheidet das Recht vom Server: Fehlt es dort, führt der Reiter nicht über die lokale
 * Erinnerung doch in dessen Kasse (Rechte entzogen, anderes Konto am selben Gerät).
 */
export function cashTabTarget(
  context: { eventId: string | null; canSell?: boolean },
  last: { eventId: string; device?: boolean } | undefined,
): string | null {
  if (context.eventId !== null && context.canSell === true) {
    return `/basare/${context.eventId}/kasse`;
  }
  if (last === undefined) return null;
  // Schnellzugang (E11) hängt am gekoppelten Gerät, nicht am Konto
  if (last.device === true) return `/kasse-geraet/${last.eventId}`;
  if (context.eventId === last.eventId) return null;
  return `/basare/${last.eventId}/kasse`;
}

/** Initialen für den runden Profil-Knopf (z. B. „AA“), ohne Namen ein neutrales Zeichen. */
function initials(firstName: string, lastName: string): string {
  const letters = `${firstName.trim().charAt(0)}${lastName.trim().charAt(0)}`.toUpperCase();
  return letters === '' ? '?' : letters;
}

export { safeNext } from '../lib/safeNext';

/** Bildmarke (dekorativ): Fläche in `currentColor`, Buchstabe in der Flächenfarbe der Oberfläche. */
export function Logo({ size = 32 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true" focusable="false">
      <rect width="64" height="64" rx="14" fill="currentColor" />
      <path
        d="M19 41V17h14c6 0 10 3 10 8 0 3-1.700 5.300-4.500 6.500C42 32.700 44 35.500 44 39c0 5-4 8-10.500 8H19z"
        fill="var(--color-surface)"
      />
    </svg>
  );
}

/**
 * Konto-Menü am Avatar (auf allen Größen und auch auf Arbeitsseiten ohne Tableiste): Profil, Anmeldungen, Einsätze, Neuigkeiten, Basare entdecken, Meine Daten, Abmelden.
 * Einfaches Aufklapp-Muster (Disclosure, kein ARIA-Menü). Escape, Klick und Fokus außerhalb schließen es, der Fokus geht zurück auf den Avatar. Liegen auf dem Gerät noch nicht
 * übertragene Kassenverkäufe, fragt es vor dem Abmelden nach.
 */
function UserMenu({
  firstName,
  lastName,
  logout,
}: {
  firstName: string;
  lastName: string;
  logout: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [directStart, setDirectStart] = useState(readDirectStart);
  const theme = useThemeChoice();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const close = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    const onPointer = (event: PointerEvent) => {
      if (root.current !== null && !root.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  async function signOut() {
    setOpen(false);
    let unsent: { count: number; unknown: boolean };
    try {
      unsent = await countUnsentSales();
    } catch {
      // Zählung nicht möglich: eigene Rückfrage, die Abmeldung bleibt möglich
      unsent = { count: 0, unknown: true };
    }
    let question: string | null = null;
    if (unsent.count === 1) {
      question =
        'Noch 1 Kassenvorgang ist nicht übertragen. Trotzdem abmelden? Er bleibt auf diesem Gerät gespeichert.';
    } else if (unsent.count > 1) {
      question = `Noch ${String(unsent.count)} Kassenvorgänge (Verkäufe/Stornos) sind nicht übertragen. Trotzdem abmelden? Sie bleiben auf diesem Gerät gespeichert.`;
    } else if (unsent.unknown) {
      question = 'Ob noch Kassenvorgänge offen sind, ließ sich nicht prüfen. Trotzdem abmelden?';
    }
    if (question !== null && !window.confirm(question)) {
      trigger.current?.focus();
      return;
    }
    await logout();
  }

  return (
    <div
      className="user-menu"
      ref={root}
      onBlur={(event) => {
        // Fokus verlässt das Menü (z. B. per Tab): schließen
        const next = event.relatedTarget;
        if (open && !(next instanceof Node && event.currentTarget.contains(next))) setOpen(false);
      }}
    >
      <button
        type="button"
        ref={trigger}
        className="avatar"
        aria-expanded={open}
        aria-controls={open ? 'user-menu-list' : undefined}
        aria-label={`Konto-Menü von ${firstName} ${lastName}`}
        title={`${firstName} ${lastName}`}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        {initials(firstName, lastName)}
      </button>
      {open && (
        <ul className="user-menu__list" id="user-menu-list">
          <li>
            <Link to="/profil" onClick={close}>
              Profil
            </Link>
          </li>
          <li>
            <Link to="/meine-anmeldungen" onClick={close}>
              Meine Anmeldungen
            </Link>
          </li>
          <li>
            <Link to="/einsaetze" onClick={close}>
              Meine Einsätze
            </Link>
          </li>
          <li>
            <Link to="/neuigkeiten" onClick={close}>
              Neuigkeiten
            </Link>
          </li>
          <li>
            <Link to="/basare-entdecken" onClick={close}>
              Basare entdecken
            </Link>
          </li>
          <li>
            <Link to="/profil/meine-daten" onClick={close}>
              Meine Daten
            </Link>
          </li>
          {isNativeApp() && (
            <li>
              <Link to="/einstellungen" onClick={close}>
                Einstellungen
              </Link>
            </li>
          )}
          {isNativeApp() && (
            <li>
              <button
                type="button"
                aria-pressed={directStart}
                onClick={() => {
                  writeDirectStart(!directStart);
                  setDirectStart(!directStart);
                }}
              >
                Beim Öffnen direkt zur Kasse: {directStart ? 'an' : 'aus'}
              </button>
            </li>
          )}
          <li className="user-menu__theme">
            <span className="user-menu__theme-label" id="user-menu-theme-label">
              Darstellung
            </span>
            <div
              className="user-menu__theme-options"
              role="group"
              aria-labelledby="user-menu-theme-label"
            >
              {THEME_CHOICES.map((choice) => (
                <button
                  key={choice}
                  type="button"
                  aria-pressed={theme === choice}
                  onClick={() => {
                    setTheme(choice);
                  }}
                >
                  <Icon name={THEME_ICONS[choice]} size={18} />
                  <span>{THEME_LABELS[choice]}</span>
                </button>
              ))}
            </div>
          </li>
          <li>
            <button
              type="button"
              onClick={() => {
                void signOut();
              }}
            >
              Abmelden
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

/** Verweis „Nachrichten“ mit Zähler der ungelesenen Gespräche (nur sichtbar, wenn es welche gibt). */
function MessagesLink({ unread, tab = false }: { unread: number; tab?: boolean }) {
  return (
    <NavLink to="/nachrichten">
      <Icon name="mail" size={tab ? 22 : 18} />
      {tab ? <span>Nachrichten</span> : 'Nachrichten'}
      {unread > 0 && (
        <span className="badge" aria-label={`${String(unread)} ungelesen`}>
          {unread}
        </span>
      )}
    </NavLink>
  );
}

function isUnder(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * Untere Leiste der App nach Rolle (U13, `navModel`): feste Reihenfolge, „Mehr“ öffnet das Blatt. Reiter mit Ziel sind
 * Verweise (aktive Seite per `aria-current`), „Nachrichten“ trägt den Zähler der ungelesenen Gespräche.
 */
function RoleTabbar({
  model,
  unread,
  moreOpen,
  onMore,
}: {
  model: NavModel;
  unread: number;
  moreOpen: boolean;
  onMore: () => void;
}) {
  const { pathname } = useLocation();
  // „Mehr“ hervorheben, wenn die Seite über „Mehr“ erreicht wurde (kein Reiter passt, aber ein Eintrag im Blatt)
  const onTab = model.tabs.some(
    (tab) => tab.to !== null && (tab.to === '/' ? pathname === '/' : isUnder(pathname, tab.to)),
  );
  const here =
    !onTab &&
    model.more.some((section) =>
      section.entries.some((entry) => entry.to !== '/' && isUnder(pathname, entry.to)),
    );
  return (
    <nav className="tabbar tabbar--role" aria-label="Hauptnavigation">
      {model.tabs.map((tab) =>
        tab.to === null ? (
          <button
            key={tab.key}
            type="button"
            className={here ? 'tabbar__more tabbar__more--here' : 'tabbar__more'}
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            onClick={onMore}
          >
            <Icon name={tab.icon} size={22} />
            <span>{tab.label}</span>
          </button>
        ) : tab.key === 'nachrichten' ? (
          <MessagesLink key={tab.key} unread={unread} tab />
        ) : (
          <NavLink key={tab.key} to={tab.to} end={tab.to === '/'}>
            <Icon name={tab.icon} size={22} />
            <span>{tab.label}</span>
          </NavLink>
        ),
      )}
    </nav>
  );
}

/** Rahmen für angemeldete und öffentliche Seiten: Kopfzeile, Navigation, Inhalt. */
/** Echte Höhe der Kopfzeile als `--header-h`: Leisten darunter setzen sich genau unter ihr fest, auch wenn sie umbricht. */
function useHeaderHeight(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const node = ref.current;
    if (node === null || typeof ResizeObserver === 'undefined') return;
    const apply = () => {
      document.documentElement.style.setProperty('--header-h', `${String(node.offsetHeight)}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(node);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--header-h');
    };
  }, [ref]);
}

/**
 * Echte Höhe der festen Tableiste als `--tabbar-h`: Leisten darüber (z. B. der Plakat-Knopf) setzen sich genau auf sie,
 * auch wenn sie durch Schriftgröße oder Sicherheitsabstand höher ausfällt.
 */
function useTabbarHeight(active: boolean): void {
  useEffect(() => {
    if (!active || typeof ResizeObserver === 'undefined') return;
    const node = document.querySelector<HTMLElement>('.tabbar');
    if (node === null) return;
    const apply = () => {
      document.documentElement.style.setProperty('--tabbar-h', `${String(node.offsetHeight)}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(node);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--tabbar-h');
    };
  }, [active]);
}

export function Layout() {
  const { status, user, logout } = useAuth();
  const header = useRef<HTMLElement>(null);
  useHeaderHeight(header);
  const location = useLocation();
  const unread = useUnreadMessages();
  const navContext = useNavContext();
  const shownContext = useNavContextOrLast();
  // Beim Seitenwechsel neu lesen: die Kasse merkt sich den zuletzt benutzten Basar lokal
  const cashTarget = cashTabTarget(
    navContext,
    useMemo(() => lastPosEvents()[0], [location.pathname]), // eslint-disable-line react-hooks/exhaustive-deps
  );
  const readyFile = useSyncExternalStore(readyFileStore.subscribe, readyFileStore.get);
  // Leiste nach Rolle (U13): In der nativen App mit den Rollen der Person, im Browser ohne Abfrage (BROWSER_ROLES)
  const native = isNativeApp();
  const signedIn = status === 'authenticated' && user !== null;
  const nativeRoles = useNavRoles(native && signedIn, user?.id ?? null);
  const roles = native ? nativeRoles : BROWSER_ROLES;
  // Das Blatt gehört zur Seite, auf der es geöffnet wurde: Seitenwechsel (auch Zurück-Taste) schließt es
  const [moreAt, setMoreAt] = useState<string | null>(null);
  const moreOpen = moreAt === location.pathname;
  const model = signedIn
    ? navModel({
        deviceSession: false,
        ...roles,
        cashTarget,
        context:
          shownContext.eventId === null
            ? null
            : {
                eventId: shownContext.eventId,
                eventTitle: shownContext.eventTitle,
                ...(shownContext.status === undefined ? {} : { status: shownContext.status }),
                ...(shownContext.permissions === undefined
                  ? {}
                  : { permissions: shownContext.permissions }),
                features: shownContext.features,
              },
      })
    : null;
  useScrollRestoration();
  useAnalyticsPageViews();
  const analytics = useAnalytics();
  // Wartende Verkäufe auch senden, wenn die Kassenseite nicht offen ist (U15a)
  useBackgroundSend();
  const showTabbar = signedIn && !HIDE_TABBAR.test(location.pathname);
  // Im Basar steht die Bereichsleiste oben (Desktop) und die Statusleiste links: die Kopfnavigation entfällt dort
  const inBasar = navContext.eventId !== null;
  // Arbeitsseiten: schlanke, einzeilige Kopfzeile (Marke und Konto), die Bereiche stehen im Menü der Seite
  const slim = HIDE_TABBAR.test(location.pathname);
  useTabbarHeight(showTabbar);
  const cash = CASH_PAGE.test(location.pathname);
  const headerClass = [
    'app-header',
    showTabbar ? 'app-header--tabbar' : '',
    slim ? 'app-header--slim' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const mainClass = [showTabbar ? 'has-tabbar' : '', readyFile === null ? '' : 'has-ready-file']
    .filter(Boolean)
    .join(' ');
  if (cash) {
    // Kassen-Modus: nur der Inhalt, die Kasse zeichnet ihre Kopfzeile selbst
    return (
      <>
        <a className="skip-link" href="#inhalt">
          Zum Inhalt springen
        </a>
        <main id="inhalt" tabIndex={-1} className="cash-main">
          {status === 'offline' && (
            <p className="cash-offline" role="status">
              Offline – Verkäufe werden gespeichert
            </p>
          )}
          <Outlet />
        </main>
        <ReadyFileBar />
      </>
    );
  }
  return (
    <>
      <a className="skip-link" href="#inhalt">
        Zum Inhalt springen
      </a>
      <header className={headerClass} ref={header}>
        <div className="app-header__inner">
          <div className="app-header__lead">
            <Link to="/" className="brand" aria-label="BARSO – zur Startseite">
              <Logo size={28} />
              <span className="brand__name">BARSO</span>
            </Link>
            {status === 'authenticated' && user !== null && !slim && <ContextSwitcher />}
          </div>
          <nav
            className="nav"
            aria-label="Hauptnavigation"
            hidden={(slim || inBasar) && status !== 'offline'}
          >
            {status === 'authenticated' && user !== null ? (
              <>
                {HEADER_NAV.map((entry) => (
                  <NavLink key={entry.to} to={entry.to} end={entry.to === '/'}>
                    <Icon name={entry.icon} size={18} />
                    {entry.label}
                  </NavLink>
                ))}
                <MessagesLink unread={unread} />
              </>
            ) : status === 'offline' ? (
              <span className="badge">Offline: Kasse, Annahme und Rückgabe arbeiten weiter</span>
            ) : (
              <>
                <NavLink to="/basare-entdecken">Basare entdecken</NavLink>
                <NavLink to="/funktionen">Funktionen</NavLink>
                <NavLink to="/hilfe">Hilfe</NavLink>
                <NavLink to="/anmelden">Anmelden</NavLink>
                <NavLink to="/registrieren" className="nav__cta">
                  Registrieren
                </NavLink>
              </>
            )}
          </nav>
          {status === 'authenticated' && user !== null && (
            <UserMenu firstName={user.firstName} lastName={user.lastName} logout={logout} />
          )}
        </div>
      </header>
      <ConnectivityBar />
      <main id="inhalt" tabIndex={-1} className={mainClass === '' ? undefined : mainClass}>
        <div className="container">
          <Outlet />
        </div>
      </main>
      {showTabbar && model !== null && (
        <>
          <RoleTabbar
            model={model}
            unread={unread}
            moreOpen={moreOpen}
            onMore={() => {
              setMoreAt(location.pathname);
            }}
          />
          {moreOpen && (
            <MoreSheet
              sections={model.more}
              onClose={() => {
                setMoreAt(null);
              }}
            />
          )}
        </>
      )}
      <ReadyFileBar />
      <ConsentBanner />
      <footer
        className={[
          'app-footer',
          showTabbar ? 'app-footer--tabbar' : '',
          readyFile === null ? '' : 'app-footer--ready-file',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="container">
          <nav className="nav" aria-label="Rechtliches">
            <Link to="/impressum">Impressum</Link>
            <Link to="/nutzungsbedingungen">Nutzungsbedingungen</Link>
            <Link to="/datenschutz">Datenschutz</Link>
            <Link to="/barrierefreiheit">Barrierefreiheit</Link>
            <Link to="/rechtliches">Weitere Rechtstexte</Link>
            {analytics.active && (
              <Link to="/datenschutz-einstellungen">Datenschutz-Einstellungen</Link>
            )}
          </nav>
          <p className="muted">
            BARSO ist kein zertifiziertes Kassensystem. Abrechnungen sind Verkaufsübersichten und
            keine steuerlichen Belege.
          </p>
          <p className="muted">
            <Link to="/hilfe">Hilfe</Link> ·{' '}
            <Link to="/neuerungen">Version {__APP_VERSION__} · Neuerungen</Link>
          </p>
        </div>
      </footer>
    </>
  );
}

/** Leiste „Datei bereit“: Antippen öffnet die zuletzt erzeugte Datei (Rückfall, falls der Download nicht startet). */
function ReadyFileBar() {
  const file = useSyncExternalStore(readyFileStore.subscribe, readyFileStore.get);
  if (file === null) return null;
  return (
    <div className="ready-file" role="status">
      <span className="ready-file__name">
        <Icon name="file" size={20} />
        {file.filename}
      </span>
      <a
        className="btn btn--small"
        href={file.url}
        download={file.filename}
        target="_blank"
        rel="noopener"
      >
        Öffnen
      </a>
      <button
        type="button"
        className="ready-file__close"
        aria-label="Hinweis schließen"
        onClick={() => {
          readyFileStore.dismiss();
        }}
      >
        ×
      </button>
    </div>
  );
}

/**
 * Ohne Netz (oder bei gestörtem Server) ist nur erreichbar, was auf dem Gerät arbeiten kann: die Vorab-Erfassung (nur lokale Liste) und die Kasse (Daten und Warteschlange
 * liegen schon hier), die Rückgabe-Station und die Warenannahme, wenn sie auf diesem Gerät schon eingerichtet sind (U15c; die
 * Annahme zeigt dann die gespeicherte Verkäuferliste, nur lesen). „Prüfen“ braucht Netz und sagt es.
 */
function OfflineGate() {
  const location = useLocation();
  const stationEvent = /^\/basare\/([^/]+)\/rueckgabe\/?$/.exec(location.pathname)?.[1] ?? null;
  const intakeEvent = /^\/basare\/([^/]+)\/annahme\/?$/.exec(location.pathname)?.[1] ?? null;
  const inspect = /^\/basare\/[^/]+\/pruefen\/?$/.test(location.pathname);
  const localEvent = stationEvent ?? intakeEvent;
  const [local, setLocal] = useState<{ key: string; exists: boolean } | null>(null);
  const key =
    localEvent === null ? null : `${stationEvent === null ? 'annahme' : 'rueckgabe'}:${localEvent}`;
  useEffect(() => {
    if (localEvent === null || key === null) return;
    const guard = { cancelled: false };
    const check = stationEvent === null ? intakeExistsOnDevice : stationExistsOnDevice;
    void check(localEvent).then((exists) => {
      if (!guard.cancelled) setLocal({ key, exists });
    });
    return () => {
      guard.cancelled = true;
    };
  }, [localEvent, stationEvent, key]);
  if (
    /^\/basare\/[^/]+\/kasse\/?$/.test(location.pathname) ||
    /^\/kasse\/?$/.test(location.pathname) ||
    // Vorab-Erfassung (Laufzettel) arbeitet nur auf dem Gerät: Ohne Netz gilt die Formprüfung der Codes
    /^\/basare\/[^/]+\/laufzettel\/?$/.test(location.pathname) ||
    // Einlass prüft nur mit Netz, die Seite selbst zeigt dann die Sichtprüfung der Uhrzeit (Entscheidung zu Frage 12)
    /^\/basare\/[^/]+\/einlass\/?$/.test(location.pathname)
  ) {
    return <Outlet />;
  }
  if (key !== null) {
    if (local?.key !== key) return <p role="status">Einen Moment bitte …</p>;
    if (local.exists) return <Outlet />;
  }
  return (
    <div className="stack">
      <h1>Kein Netz</h1>
      <p>
        {inspect
          ? 'Prüfen braucht Netz: Ohne Verbindung lässt sich nicht nachsehen, was ein Etikett ist. '
          : 'Ohne Netz sind nur die Kasse und die auf diesem Gerät schon eingerichtete Rückgabe und Annahme verfügbar. '}
        Sobald das Gerät wieder online ist, geht es hier weiter.
      </p>
      <div className="row">
        <Link className="btn" to="/kasse">
          Zur Kasse
        </Link>
      </div>
    </div>
  );
}

/** Schützt Seiten: ohne Anmeldung geht es zur Anmeldung und danach zurück. */
export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <p role="status">Einen Moment bitte …</p>;
  if (status === 'offline') return <OfflineGate />;
  if (status === 'anonymous') {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/anmelden?next=${encodeURIComponent(next)}`} replace />;
  }
  return <Outlet />;
}
